import { Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { colors } from '../constants/colors.constant';
import { Angular5Csv } from 'angular5-csv/Angular5-csv';
import { QuestBlockFilteringService } from './quest-block-filtering.service';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs/Rx';
import { ReportingService } from '../services/reporting.service';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { IndividualsService } from '../services/individuals.service';
import { AlertService } from '../alerts/alert.service';
import { PermissionsService } from '../services/permissions.service';
import { TranslationService } from '../services/translation.service';

@Component({
  selector: 'app-featured-child-report',
  templateUrl: 'featured-child-report.component.html'
})
export class FeaturedChildReportComponent implements OnInit, OnDestroy {
  public featuredChildrenListLoading = false;
  public questBlocks = [];
  private questBlockFilteringSubscription: Subscription;
  public triggerChange = 0;
  public chosenUser: any = [];
  public plotMultipleStudentsControl = new FormControl(false);
  featuredChildControl = new FormControl();
  featuredChildOptions: any = [];
  filteredOptions: Observable<any[]>;

  public wellbeingGraph: any = {
    loading: true,
    submissionCount: 0,
    categories: [
      'Overall Wellbeing',
      'Attachment to School',
      'Self-regulation',
      'Social confidence',
      'Supportive Relationships',
    ],
    colors: [
      colors.thriving,
      colors.ontrack,
      colors.atrisk,
      colors.vulnerable,
      colors.population,
      colors.groupMedian
    ],
    series: [
      {
        data: [],
        id: 'thriving',
        name: ' ',
        type: 'column'
      },
      {
        data: [],
        id: 'ontrack',
        name: ' ',
        type: 'column'
      },
      {
        data: [],
        id: 'atrisk',
        name: ' ',
        type: 'column'
      },
      {
        data: [],
        id: 'vulnerable',
        name: ' ',
        type: 'column'
      },
      {
        data: [],
        id: 'population',
        name: 'Population Benchmark',
        type: 'spline'
      },
      // {
      //   data: [],
      //   id: 'group',
      //   name: 'Population Median',
      //   type: 'spline'
      // },
    ],
  };
  public executiveGraph = {
    loading: true,
    submissionCount: 0,
    categories: [
      'Spatial Span Working Memory',
      'Inhibitory Control',
      'Cognitive Flexibility',
      'Attention Control',
      'Focused Attention',
    ],
    colors: [
      colors.thrivingDark,
      colors.ontrackDark,
      colors.atriskDark,
      colors.vulnerableDark,
      colors.population,
      colors.groupMedian
    ],
    series: [
      {
        data: [],
        id: 'aboveexpected',
        name: ' ',
        type: 'column',
      },
      {
        data: [],
        id: 'belowexpected',
        name: ' ',
        type: 'column'
      },
      {
        data: [],
        id: 'population',
        name: 'Population Benchmark',
        type: 'spline'
      },
      // {
      //   data: [],
      //   id: 'group',
      //   name: 'Population Average',
      //   type: 'spline'
      // },
    ],
  };
  public permissions = [];
  public fullReportResponse: any;
  public translations: any = {};

  constructor(
    private store: Store<any>,
    private questBlockFilteringService: QuestBlockFilteringService,
    private individualsService: IndividualsService,
    private alertService: AlertService,
    private reportingService: ReportingService,
    private translationService: TranslationService,
    private permissionService: PermissionsService
  ) {
    this.translations = translationService.getTranslations();
    this.permissions = this.permissionService.getPermissions();
    this.resetGraphData();

    // Subscribe to store and store in public property
    this.questBlockFilteringSubscription = this.store.subscribe(val => {
      if (typeof val.questBlockFilter !== 'undefined') {
        this.resetGraphData();
        this.questBlocks = val.questBlockFilter;
        this.getIndividualsByQuestBlock();
        this.refresh();
      }
    });
  }

  ngOnInit() {
    this.questBlocks = this.questBlockFilteringService.questBlocks;
    this.filteredOptions = this.featuredChildControl.valueChanges
      .pipe(
        startWith<string | any>(''),
        map(value => typeof value === 'string' ? value : value.FullName),
        map(FullName => FullName ? this._filter(FullName) : this.featuredChildOptions.slice())
      );

    // Subscribe to form changes
    const __this = this;
    this.featuredChildControl.valueChanges
      .subscribe(function (option) {
        if (typeof option.FullName === 'undefined') {
          return;
        }

        // If user is not plotting multiple students, reset the array
        if (__this.plotMultipleStudentsControl.value === false) {
          __this.chosenUser = [];
        }

        __this.chosenUser.push(option);
        __this.refresh(); // call refresh with user object
      });

    // Get Individuals by Quest Block
    this.getIndividualsByQuestBlock();
  }

  ngOnDestroy() {
    if (this.questBlockFilteringSubscription) {
      this.questBlockFilteringSubscription.unsubscribe();
    }
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.featuredChildOptions.filter(function (option) {
      return option.FullName.toLowerCase().indexOf(filterValue) === 0;
    });
  }

  displayFn(user?: any): string | undefined {
    return user ? user.FullName : undefined;
  }

  public resetGraphData() {
    console.log('reset graph data');
    const __this = this;

    this.fullReportResponse = null;
    this.questBlocks = [];
    this.chosenUser = [];
    this.featuredChildOptions = [];

    this.wellbeingGraph = {
      loading: true,
      submissionCount: 0,
      categories: [
        'Overall Wellbeing',
        'Attachment to School',
        'Self-regulation',
        'Social Confidence',
        'Supportive Relationships',
      ],
      colors: [
        colors.thriving,
        colors.ontrack,
        colors.atrisk,
        colors.vulnerable,
        colors.population,
        colors.groupMedian
      ],
      series: [
        {
          data: [],
          id: 'thriving',
          name: ' ',
          type: 'column'
        },
        {
          data: [],
          id: 'ontrack',
          name: ' ',
          type: 'column'
        },
        {
          data: [],
          id: 'atrisk',
          name: ' ',
          type: 'column'
        },
        {
          data: [],
          id: 'vulnerable',
          name: ' ',
          type: 'column'
        },
        {
          data: [],
          id: 'population',
          name: 'Population Benchmark',
          type: 'spline'
        },
        // {
        //   data: [],
        //   id: 'group',
        //   name: 'Population Median',
        //   type: 'spline'
        // },
      ],
    };

    this.executiveGraph = {
      loading: true,
      submissionCount: 0,
      categories: [
        'Spatial Span Working Memory',
        'Inhibitory Control',
        'Cognitive Flexibility',
        'Attention Control',
        'Focused Attention',
      ],
      colors: [
        colors.thrivingDark,
        colors.ontrackDark,
        colors.atriskDark,
        colors.vulnerableDark,
        colors.population,
        colors.groupMedian
      ],
      series: [
        {
          data: [],
          id: 'aboveexpected',
          name: ' ',
          type: 'column',
        },
        {
          data: [],
          id: 'belowexpected',
          name: ' ',
          type: 'column'
        },
        {
          data: [],
          id: 'population',
          name: 'Population Benchmark',
          type: 'spline'
        },
        // {
        //   data: [],
        //   id: 'group',
        //   name: 'Population Average',
        //   type: 'spline'
        // },
      ],
    };

    this.triggerChange++; // triggers child chart refresh
  }

  public getIndividualsByQuestBlock() {
    if (this.questBlocks.length === 0) {
      return; // only if quest blocks are set should it be run
    }

    this.featuredChildrenListLoading = true;

    const requestParams = [];
    this.questBlocks.forEach(function (val) {
      requestParams[val.name] = val.value;
    });

    this.individualsService.getIndividualsInQuestBlocks(requestParams)
      .then(individuals => {
        this.featuredChildOptions = individuals;
        this.triggerChange++; // triggers child chart refresh
        this.featuredChildrenListLoading = false;
      })
      .catch((msg) => {
        this.alertService.show(msg.error.message, 'danger');
        this.featuredChildrenListLoading = false;
      });
  }

  public refresh() {
    if (this.questBlocks.length === 0 || this.chosenUser.length === 0) {
      return; // only if quest blocks are set should it be run
    }

    const requestParams = [];
    requestParams['questBlocks'] = this.questBlocks.map(function(k) { return k.value; }).join(',');
    requestParams['studentIds'] = this.chosenUser.map(function(k) { return k.id; });
    requestParams['studentIds'] = Array.from(new Set(requestParams['studentIds'])); // remove dupes
    requestParams['studentIds'] = requestParams['studentIds'].join(',');

    this.reportingService.getFeaturedChildSummaryData(requestParams)
      .then(report => {
        this.fullReportResponse = report;
        this.wellbeingGraph = this.turnGroupSummaryResponseIntoGraphableData(
          report,
          'wellbeingData',
          'studentWellbeingData',
          'ScorePercentileRelativeToPopulation',
          this.wellbeingGraph
        );
        this.executiveGraph = this.turnGroupSummaryResponseIntoGraphableData(
          report,
          'executiveData',
          'studentExecutiveData',
          'ScoreAsPercentageOfMaxScore',
          this.executiveGraph
        );
        this.triggerChange++; // triggers child chart refresh
      })
      .catch((msg) => {
        console.log(msg);
        this.alertService.show(msg.error.message, 'danger');
      });
  }

  public turnGroupSummaryResponseIntoGraphableData(report, responseDataKey, studentResponseDataKey, studentPercentileKey, graphObject) {
    console.log(report, responseDataKey);

    // Reset series (keep only initial 6, remove student lines)
    graphObject.series = graphObject.series.slice(0, 6);
    for (let i = 0; i < graphObject.series.length; i++) {
      graphObject.series[i].data = [];
    }

    const accrualData = {
      ThrivingPercentile: [],
      OnTrackPercentile: [],
      AtRiskPercentile: [],
      VulnerablePercentile: [],
      PopulationBenchmark: [],
      BelowExpectedLevelPercentageOfGroup: [],
      AboveExpectedLevelPercentageOfGroup: [],
      GroupAverage: [],
      PopulationAverage: [],
      GroupTotalCount: 0,
    };

    const groupDataByTitle = {};
    if (report[responseDataKey]) {
      for (let i = 0; i < report[responseDataKey].length; i++) {
        const item = report[responseDataKey][i];
        groupDataByTitle[item.Title] = item;
      }
    }

    // Align by category title
    for (let i = 0; i < graphObject.categories.length; i++) {
      const categoryTitle = graphObject.categories[i];
      const item = groupDataByTitle[categoryTitle];

      if (!item) {
        console.warn('No data for category: ' + categoryTitle);
        if (responseDataKey === 'wellbeingData') {
          accrualData.ThrivingPercentile.push(null);
          accrualData.OnTrackPercentile.push(null);
          accrualData.AtRiskPercentile.push(null);
          accrualData.VulnerablePercentile.push(null);
          accrualData.PopulationBenchmark.push(null);
        } else {
          accrualData.AboveExpectedLevelPercentageOfGroup.push(null);
          accrualData.BelowExpectedLevelPercentageOfGroup.push(null);
          accrualData.PopulationBenchmark.push(null);
        }
        continue;
      }

      if (responseDataKey === 'wellbeingData') {
        accrualData.ThrivingPercentile.push(10);
        accrualData.OnTrackPercentile.push(65);
        accrualData.AtRiskPercentile.push(15);
        accrualData.VulnerablePercentile.push(10);
        accrualData.PopulationBenchmark.push(50);
      } else {
        accrualData.AboveExpectedLevelPercentageOfGroup.push(84);
        accrualData.BelowExpectedLevelPercentageOfGroup.push(16);
        accrualData.PopulationBenchmark.push(50);
      }
    }

    if (report[responseDataKey] && report[responseDataKey][0]) {
      accrualData.GroupTotalCount = report[responseDataKey][0]['GroupTotalCount'];
    }

    // Apply to graph
    if (responseDataKey === 'wellbeingData') {
      graphObject.series[0].data = accrualData.ThrivingPercentile;
      graphObject.series[1].data = accrualData.OnTrackPercentile;
      graphObject.series[2].data = accrualData.AtRiskPercentile;
      graphObject.series[3].data = accrualData.VulnerablePercentile;
      graphObject.series[4].data = accrualData.PopulationBenchmark;
    } else {
      graphObject.series[0].data = accrualData.AboveExpectedLevelPercentageOfGroup;
      graphObject.series[1].data = accrualData.BelowExpectedLevelPercentageOfGroup;
      graphObject.series[2].data = accrualData.PopulationBenchmark;
    }

    graphObject.submissionCount = accrualData.GroupTotalCount;
    graphObject.loading = false;

    // Hide group line if too few submissions
    if (accrualData.GroupTotalCount < 10) {
      for (let i = 0; i < graphObject.series.length; i++) {
        if (graphObject.series[i].id === 'group') {
          graphObject.series[i].visible = false;
        }
      }
    }

    // Remove previous student lines
    graphObject.series = graphObject.series.filter(function(item) {
      return item.id.indexOf('student') === -1;
    });

    // Add new student lines
    const studentArray = [];

    if (report[studentResponseDataKey]) {
      for (let i = 0; i < report[studentResponseDataKey].length; i++) {
        const studentEntry = report[studentResponseDataKey][i];
        for (const key in studentEntry.data) {
          if (!studentEntry.data.hasOwnProperty(key)) continue;

          const value = studentEntry.data[key];

          const groupedValue = {};
          for (let v = 0; v < value.length; v++) {
            const entry = value[v];
            if (!groupedValue[entry.StudentQuestBlockId]) {
              groupedValue[entry.StudentQuestBlockId] = [];
            }
            groupedValue[entry.StudentQuestBlockId].push(entry);
          }

          const studentQuestBlockKeys = Object.keys(groupedValue);
          for (let j = 0; j < studentQuestBlockKeys.length; j++) {
            const studentLoop = [];
            const loop = groupedValue[studentQuestBlockKeys[j]];
            let submissionDate = '';

            // Create map of score by Title for this submission
            const scoreByTitle = {};
            for (let s = 0; s < loop.length; s++) {
              const entry = loop[s];
              submissionDate = entry.SubmissionDate;
              scoreByTitle[entry.Title] = parseFloat(entry[studentPercentileKey]);
            }

            // Align to categories
            for (let c = 0; c < graphObject.categories.length; c++) {
              const category = graphObject.categories[c];
              if (scoreByTitle.hasOwnProperty(category)) {
                studentLoop.push(scoreByTitle[category]);
              } else {
                studentLoop.push(null); // placeholder if student didn't answer that category
              }
            }

            studentArray.push({
              studentId: studentEntry.studentId,
              studentName: studentEntry.student.student_name.FullName + ' (' + key + ', ' + formatDate(submissionDate, 'dd MMMM yyyy', 'en') + ')',
              data: studentLoop
            });
          }
        }
      }
    }

    for (let i = 0; i < studentArray.length; i++) {
      const studentItem = studentArray[i];
      graphObject.series.push({
        data: studentItem.data,
        id: 'student' + studentItem.studentId,
        name: studentItem.studentName,
        type: 'spline',
        color: '#000'
      });
    }

    return graphObject;
  }

  public print() {
    window.print();
  }
}
